import { todayIso } from "@/modules/billing/billingDisplayUtils";
import { toneClass } from "@/modules/shared/status";
import type { ClientInvoice, Ticket } from "@/modules/types";

export type TicketInvoiceBadgeItem = {
  key: string;
  label: string;
  className: string;
};

const isInvoiceOverdue = (invoice: ClientInvoice) =>
  invoice.status === "sent" &&
  Boolean(invoice.due_date) &&
  invoice.due_date < todayIso();

/** A submitted bank (ACH) payment that has not cleared yet. */
const isInvoicePaymentProcessing = (invoice: ClientInvoice) =>
  Boolean(invoice.payment_processing_at) &&
  invoice.status !== "paid" &&
  invoice.status !== "void";

const isInvoiceScheduled = (invoice: ClientInvoice) =>
  invoice.status === "draft" && Boolean(invoice.scheduled_send_at?.trim());

export const summarizeTicketInvoices = (invoices: ClientInvoice[]) => {
  const summary = {
    paid: 0,
    processing: 0,
    pending: 0,
    overdue: 0,
    draft: 0,
    scheduled: 0,
    voided: 0,
    total: invoices.length,
  };

  for (const invoice of invoices) {
    // Paid by bank, waiting to clear: neither awaiting payment nor overdue.
    if (isInvoicePaymentProcessing(invoice)) {
      summary.processing += 1;
      continue;
    }
    switch (invoice.status) {
      case "paid":
        summary.paid += 1;
        break;
      case "void":
        summary.voided += 1;
        break;
      case "draft":
        if (isInvoiceScheduled(invoice)) {
          summary.scheduled += 1;
        } else {
          summary.draft += 1;
        }
        break;
      case "sent":
      case "overdue":
        if (isInvoiceOverdue(invoice) || invoice.status === "overdue") {
          summary.overdue += 1;
        } else {
          summary.pending += 1;
        }
        break;
      default:
        break;
    }
  }

  return summary;
};

const countLabel = (count: number, singular: string, plural: string) =>
  count === 1 ? singular : `${count} ${plural}`;

export const getTicketInvoiceBadges = (
  ticket: Ticket,
  invoices: ClientInvoice[],
): TicketInvoiceBadgeItem[] => {
  if (invoices.length === 0) {
    if (ticket.invoice_id && ticket.delivery_status === "invoice_sent") {
      return [
        {
          key: "awaiting-payment",
          label: "Awaiting payment",
          className: `rounded-none ${toneClass("warning", "solid")}`,
        },
      ];
    }
    return [];
  }

  const summary = summarizeTicketInvoices(invoices);
  const badges: TicketInvoiceBadgeItem[] = [];

  if (summary.overdue > 0) {
    badges.push({
      key: "overdue",
      label: countLabel(summary.overdue, "Overdue", "overdue"),
      className: `rounded-none ${toneClass("destructive", "solid")}`,
    });
  }

  if (summary.processing > 0) {
    badges.push({
      key: "processing",
      label: countLabel(
        summary.processing,
        "Payment processing",
        "payments processing",
      ),
      className: `rounded-none ${toneClass("warning", "solid")}`,
    });
  }

  if (summary.pending > 0) {
    badges.push({
      key: "pending",
      label: countLabel(
        summary.pending,
        "Awaiting payment",
        "awaiting payment",
      ),
      className: `rounded-none ${toneClass("warning", "solid")}`,
    });
  }

  if (summary.scheduled > 0) {
    badges.push({
      key: "scheduled",
      label: countLabel(summary.scheduled, "Scheduled", "scheduled"),
      className: `rounded-none ${toneClass("info", "solid")}`,
    });
  }

  if (summary.draft > 0) {
    badges.push({
      key: "draft",
      label: countLabel(summary.draft, "Draft", "drafts"),
      className: `rounded-none ${toneClass("muted", "solid")}`,
    });
  }

  if (summary.paid > 0) {
    badges.push({
      key: "paid",
      label: countLabel(summary.paid, "Paid", "paid"),
      className: `rounded-none ${toneClass("success", "solid")}`,
    });
  }

  if (
    summary.paid > 0 &&
    summary.pending === 0 &&
    summary.overdue === 0 &&
    ticket.delivery_status === "delivered"
  ) {
    badges.push({
      key: "delivered",
      label: "Delivered",
      className: `rounded-none ${toneClass("success", "solid")}`,
    });
  } else if (
    summary.paid > 0 &&
    summary.pending === 0 &&
    summary.overdue === 0 &&
    ticket.delivery_status !== "delivered" &&
    ticket.delivery_status !== "none"
  ) {
    badges.push({
      key: "delivery-pending",
      label: "Pending delivery",
      className:
        "rounded-none border-transparent bg-violet-600 text-white dark:bg-violet-500",
    });
  }

  return badges.slice(0, 3);
};

export const ticketHasUnpaidInvoice = (
  ticket: Ticket,
  invoices: ClientInvoice[],
) => {
  if (invoices.length > 0) {
    const summary = summarizeTicketInvoices(invoices);
    return summary.pending > 0 || summary.overdue > 0;
  }
  return (
    Boolean(ticket.invoice_id) && ticket.delivery_status === "invoice_sent"
  );
};
